// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta } from '../types';
import { glossaryTerm } from '../glossary';
import { formatDate, formatNumber } from '../utils/format';

/** About modal: what this is, where the data comes from, and what it cannot tell you. */
export function createAbout(meta: Meta): { open: () => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About Vehicle Fleet');

  const n = meta.national;
  modal.innerHTML = `
    <button class="modal-close" aria-label="Close">✕</button>
    <h2>About Vehicle Fleet</h2>
    <p>
      This is every road vehicle ${glossaryTerm('registered', 'registered')} in Australia on ${meta.snapshotDate} —
      all <strong>${formatNumber(n.totalAll)}</strong> of them, of which <strong>${formatNumber(n.passengerTotal)}</strong>
      are ${glossaryTerm('passenger', 'passenger vehicles')} — broken down by fuel type, year of manufacture, make and
      postcode.
    </p>
    <p>
      It exists to separate two things a single headline blurs. Everyone has read that electric-car <em>sales</em> are
      surging. Far fewer people know that battery-electric cars are still only
      <strong>${n.bevShare.toFixed(2)}%</strong> of the cars actually on the road — because the fleet is a
      ${glossaryTerm('stock', 'slow-moving stock')}, not the latest year of sales. Drag the slider in the New-Car Effect
      view to watch the difference for yourself.
    </p>

    <h3>Where the data comes from</h3>
    <ul>
      <li>
        <strong>BITRE — Road Vehicles Australia</strong>, the January 2025 registry snapshot, and the January
        2021–2025 snapshots for the trend. It replaced the ABS Motor Vehicle Census, discontinued in 2021, and is
        derived from the state and territory motor-vehicle registries via the national NEVDIS system.
      </li>
      <li><strong>ABS ASGS 2021</strong> — postal-area boundaries for the map.</li>
      <li><strong>matthewproctor/australianpostcodes</strong> — postcode → locality and coordinates.</li>
    </ul>

    <h3>How often it updates</h3>
    <p>
      An automated pipeline rebuilds the site once a year, matching BITRE's annual publication cycle. Data last built
      ${formatDate(meta.generated)} (the ${meta.snapshotDate} snapshot).
    </p>

    <h3>Important limits — please read</h3>
    <ul>
      <li>
        <strong>This is the fleet, not sales.</strong> ${glossaryTerm('stock', 'A stock, not a flow')}: it counts what
        is registered now. New-vehicle sales figures (published separately by the industry) run far ahead on electric
        share. The "New-Car Effect" view approximates the sales story using ${glossaryTerm('yom', 'year of manufacture')}.
      </li>
      <li>
        <strong>Counts are ${glossaryTerm('perturbation', 'confidentialised')}.</strong> Small cells are randomly
        nudged and cells under three are suppressed, so detailed numbers will not sum exactly to the headline totals.
        National and state figures are reliable; a single rare fuel in a tiny postcode may be slightly off.
      </li>
      <li>
        <strong>"Electric" means ${glossaryTerm('bev', 'battery-electric or fuel-cell')}.</strong>
        ${glossaryTerm('hybrid', 'Hybrids')} still burn fuel and are counted separately;
        ${glossaryTerm('electrified', '"electrified"')} means hybrid + electric together.
      </li>
      <li>
        Postal areas approximate postcodes, and PO-box-only postcodes have no boundary, so a few postcodes appear in
        the tables but not on the map. Fuel-share figures on the map and rankings are for passenger vehicles.
      </li>
    </ul>

    <h3>Licence</h3>
    <p>
      BITRE and ABS data are published under CC BY 4.0. This site is an independent project and is not affiliated with
      either agency.
    </p>
  `;

  document.body.append(overlay, modal);
  const close = () => {
    overlay.classList.remove('open');
    modal.classList.remove('open');
  };
  const open = () => {
    overlay.classList.add('open');
    modal.classList.add('open');
    (modal.querySelector('.modal-close') as HTMLElement)?.focus();
  };
  overlay.addEventListener('click', close);
  modal.querySelector('.modal-close')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });
  return { open, close };
}
